import { getLogger } from "@expert/logging";
import { Container, Flex, useMantineTheme } from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { useEffect } from "react";
import { withErrorBoundary } from "react-error-boundary";

import type { ExpertUniversitySimulatedVoiceTask } from "@/expert-university";
import type { AgentActivity, AgentSdk, CustomerSession, TrainingTask, WithoutCustomerSession } from "@/sdk";

import { ChatManager } from "@/components/chat";
import styles from "@/components/workspace/workspace.module.css";
import { env } from "@/config";
import { useExpertUniversityWebsocket } from "@/expert-university/hooks/useExpertUniversityWebsocket";
import { NotificationContainer } from "@/notification/NotificationContainer";
import { Panels } from "@/panels";
import { MicroLearning } from "@/sales-tools";
import { Announcement } from "@/sales-tools/challenges/Announcement";
import { ConfettiAnimation } from "@/sales-tools/reactions/Confetti";
import { createFallbackRender } from "@/shared-utils";
import { Troubleshooting } from "@/troubleshoot";
import { APP_START, trackTimeElapsed } from "@/utils/trackTimeElapsed";

import { useWorkspaceContentPanels } from "./useWorkspaceContentPanels";
import { WorkspaceContentLeftSidebarSlot } from "./WorkspaceContentLeftSidebarSlot";
import { WorkspaceContentLeftTopASlot } from "./WorkspaceContentLeftTopASlot";
import { WorkspaceContentRightSidebarSlot } from "./WorkspaceContentRightSidebarSlot";
import { WorkspaceContentTopbarSlot } from "./WorkspaceContentTopbarSlot";

// Fallback logger for error boundaries (created at module level for stability)
const fallbackLogger = getLogger({ module: "WorkspaceContent" });

const MicroLearningModalHandled = withErrorBoundary(MicroLearning, {
    fallbackRender: createFallbackRender(fallbackLogger),
});
const AnnouncementModalHandled = withErrorBoundary(Announcement, {
    fallbackRender: createFallbackRender(fallbackLogger),
});

type WorkspaceContentProps = {
    activeExpertUniversityTask: ExpertUniversitySimulatedVoiceTask | null;
    activeTrainingTask: TrainingTask | null;
    callControlsEnabled: boolean;
    hasActiveVoiceTask: boolean;
    hasCallbackWaiting: boolean;
    isOutboundEnabled: boolean | undefined;
    isRealCustomerPanelEnabledForTraining: boolean | undefined;
    challengeEnabled: boolean;
    currentActivity: AgentActivity | undefined;
    customerPanelEnabled: boolean;
    customerSession: boolean;
    hasCallEnded: boolean;
    microLearningV2Enabled: undefined | boolean;
    showRealtimeTranscript: boolean;
    sdk: AgentSdk;
    session: CustomerSession | WithoutCustomerSession;
    showExpertUniversity: undefined | boolean;
    troubleshooting: boolean;
};

export function WorkspaceContent({
    activeExpertUniversityTask,
    activeTrainingTask,
    callControlsEnabled,
    hasActiveVoiceTask,
    hasCallbackWaiting,
    isOutboundEnabled,
    isRealCustomerPanelEnabledForTraining,
    challengeEnabled,
    currentActivity,
    customerPanelEnabled,
    customerSession,
    hasCallEnded,
    microLearningV2Enabled,
    sdk,
    session: { startedTimestamp, id: sessionId },
    showExpertUniversity,
    showRealtimeTranscript,
    troubleshooting,
}: WorkspaceContentProps) {
    useExpertUniversityWebsocket();
    const theme = useMantineTheme();
    const isLargeScreen = useMediaQuery(`(min-width: ${theme.breakpoints.md})`);
    const { flowSlots, guideToolboxCommand, leftBottomConfig, leftTopConfig, rightBottomConfig, rightTopConfig } =
        useWorkspaceContentPanels({
            customerSession,
            isLargeScreen,
            logger: fallbackLogger,
            sessionId,
        });

    useEffect(() => {
        trackTimeElapsed(APP_START, "timeToPanelsRendered");
    }, []);

    return (
        <Flex flex={1}>
            {env.channel === "chat" ? (
                <ChatManager />
            ) : (
                <Panels
                    // Aside Panels
                    leftSidebarSlot={
                        <WorkspaceContentLeftSidebarSlot
                            showRealtimeTranscript={showRealtimeTranscript}
                            taskStartTimeInMs={startedTimestamp}
                        />
                    }
                    leftSidebarConfig={{
                        isCollapsed: Boolean(showExpertUniversity),
                    }}
                    topbarSlot={<WorkspaceContentTopbarSlot />}
                    topbarConfig={{
                        isCollapsed:
                            Boolean(showExpertUniversity) ||
                            !callControlsEnabled ||
                            (!isOutboundEnabled && !hasActiveVoiceTask && !hasCallbackWaiting),
                    }}
                    rightSidebarSlot={
                        <WorkspaceContentRightSidebarSlot
                            activeExpertUniversityTask={activeExpertUniversityTask}
                            customerSession={customerSession}
                            customerPanelEnabled={customerPanelEnabled}
                            isRealCustomerPanelEnabledForTraining={isRealCustomerPanelEnabledForTraining}
                        />
                    }
                    rightSidebarConfig={{
                        isCollapsed: Boolean(showExpertUniversity),
                    }}
                    // Main Panels
                    leftTopASlot={
                        <WorkspaceContentLeftTopASlot
                            showExpertUniversity={showExpertUniversity}
                            activeTrainingTask={activeTrainingTask}
                            guideToolboxCommand={guideToolboxCommand}
                        />
                    }
                    leftTopBSlot={flowSlots.leftTopBSlot}
                    leftTopConfig={leftTopConfig}
                    rightBottomASlot={flowSlots.rightBottomASlot}
                    rightBottomConfig={rightBottomConfig}
                    rightTopASlot={flowSlots.rightTopASlot}
                    rightTopConfig={rightTopConfig}
                    leftBottomBSlot={flowSlots.leftBottomBSlot}
                    leftBottomConfig={leftBottomConfig}
                />
            )}

            {/* Modals go outside Panels */}

            {/* Modals */}
            {(challengeEnabled || microLearningV2Enabled) &&
            hasCallEnded &&
            !customerSession &&
            currentActivity === sdk.availableAgentActivity ? (
                <>
                    <AnnouncementModalHandled />
                    <MicroLearningModalHandled />
                </>
            ) : null}

            {/* Modal */}
            {(challengeEnabled || microLearningV2Enabled) && currentActivity === sdk.defaultAgentActivity && (
                <AnnouncementModalHandled />
            )}

            {/* Modal */}
            {troubleshooting ? <Troubleshooting /> : null}

            {/* Notifications are global and outside of Panels */}
            <NotificationContainer />

            <Container className={styles.confettiContainer} key={sessionId}>
                <ConfettiAnimation />
            </Container>
        </Flex>
    );
}
